import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Settings,
  BarChart3,
  TrendingUp,
  AlertCircle,
  CheckCircle,
  XCircle,
  ChevronDown,
  ChevronRight,
  Search,
  Filter,
  Eye,
  EyeOff,
  Trash2
} from 'lucide-react';
import { 
  HierarchicalMappingResponse,
  HierarchicalLayer,
  HierarchicalDomain,
  HierarchicalCategory,
  HierarchicalTechnique,
  FrameworkMapping,
  CoverageReport,
  LayerCoverage
} from '../types/hierarchical';
import { hierarchicalMappingsApi, evaluationMappingsApi, evaluationFrameworksApi } from '../services/api';
import { EvaluationMappingCreate, EvaluationMappingUpdate } from '../types';

interface EvaluationMappingsOverviewProps {
  addNotification: (message: string, type: 'success' | 'error' | 'info' | 'warning') => void;
}

const EvaluationMappingsOverview: React.FC<EvaluationMappingsOverviewProps> = ({ addNotification }) => {
  const [data, setData] = useState<HierarchicalMappingResponse | null>(null);
  const [coverageReport, setCoverageReport] = useState<CoverageReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFrameworks, setSelectedFrameworks] = useState<number[]>([]);
  const [expandedLayers, setExpandedLayers] = useState<Set<string>>(new Set());
  const [expandedDomains, setExpandedDomains] = useState<Set<number>>(new Set());
  const [expandedCategories, setExpandedCategories] = useState<Set<number>>(new Set());
  const [expandedTechniques, setExpandedTechniques] = useState<Set<number>>(new Set());
  const [viewMode, setViewMode] = useState<'overview' | 'comparison'>('overview');
  const [filters, setFilters] = useState({
    status: 'all', // all, approved, pending, declined
    coverage: 'all', // all, complete, partial, missing
    layer: 'all', // all, L1, L2, L3, L4, L5
    framework: 'all' // all, ragas, openai, legacy
  });
  const [showFilters, setShowFilters] = useState(false);
  const [showCreateMappingModal, setShowCreateMappingModal] = useState(false);
  const [showEditMappingModal, setShowEditMappingModal] = useState(false);
  const [selectedTechnique, setSelectedTechnique] = useState<HierarchicalTechnique | null>(null);
  const [selectedFramework, setSelectedFramework] = useState<number | null>(null);
  const [frameworkMetrics, setFrameworkMetrics] = useState<any[]>([]);
  const [selectedMetrics, setSelectedMetrics] = useState<number[]>([]);
  const [mappingFormData, setMappingFormData] = useState({
    metric_type: '',
    is_required: true,
    weight: 1.0,
    priority: 1,
    description: ''
  });
  const [editingMappingId, setEditingMappingId] = useState<number | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [hierarchicalData, coverageData] = await Promise.all([
        hierarchicalMappingsApi.getHierarchicalView(),
        hierarchicalMappingsApi.getCoverageReport()
      ]);
      
      console.log('Hierarchical data:', hierarchicalData);
      console.log('Coverage data:', coverageData);
      console.log('Framework coverage structure:', coverageData.framework_coverage);
      
      setData(hierarchicalData);
      setCoverageReport(coverageData);
      
      // Initialize with all frameworks selected
      const frameworkIds = Object.keys(hierarchicalData.statistics.frameworks).map(Number);
      setSelectedFrameworks(frameworkIds);
      
      // Expand first layer by default
      const firstLayer = Object.keys(hierarchicalData.layers)[0];
      if (firstLayer) {
        setExpandedLayers(new Set([firstLayer]));
      }
      
    } catch (error: any) {
      addNotification('Failed to load evaluation mappings data', 'error');
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleLayer = (layerName: string) => {
    const newExpanded = new Set(expandedLayers);
    if (newExpanded.has(layerName)) {
      newExpanded.delete(layerName);
    } else {
      newExpanded.add(layerName);
    }
    setExpandedLayers(newExpanded);
  };

  const toggleDomain = (domainId: number) => {
    const newExpanded = new Set(expandedDomains);
    if (newExpanded.has(domainId)) {
      newExpanded.delete(domainId);
    } else {
      newExpanded.add(domainId);
    }
    setExpandedDomains(newExpanded);
  };

  const toggleCategory = (categoryId: number) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryId)) {
      newExpanded.delete(categoryId);
    } else {
      newExpanded.add(categoryId);
    }
    setExpandedCategories(newExpanded);
  };

  const toggleTechnique = (techniqueId: number) => {
    const newExpanded = new Set(expandedTechniques);
    if (newExpanded.has(techniqueId)) {
      newExpanded.delete(techniqueId);
    } else {
      newExpanded.add(techniqueId);
    }
    setExpandedTechniques(newExpanded);
  };

  const toggleFramework = (frameworkId: number) => {
    const newSelected = [...selectedFrameworks];
    const index = newSelected.indexOf(frameworkId);
    if (index > -1) {
      newSelected.splice(index, 1);
    } else {
      newSelected.push(frameworkId);
    }
    setSelectedFrameworks(newSelected);
  };

  const getFrameworkColor = (frameworkType: string) => {
    switch (frameworkType.toLowerCase()) {
      case 'ragas':
        return 'bg-blue-900/50 text-blue-200';
      case 'openai judge':
        return 'bg-green-900/50 text-green-200';
      case 'legacy':
        return 'bg-amber-900/50 text-amber-200';
      default:
        return 'bg-slate-600 text-slate-200';
    }
  };

  const getLayerIcon = (layerName: string) => {
    if (layerName.includes('L1')) return '📘';
    if (layerName.includes('L2')) return '🔍';
    if (layerName.includes('L3')) return '🧠';
    if (layerName.includes('L4')) return '💬';
    if (layerName.includes('L5')) return '🧱';
    return '📊';
  };

  const getDomainIcon = (domainName: string) => {
    if (domainName.includes('Knowledge')) return '🧩';
    if (domainName.includes('RAG')) return '🔍';
    if (domainName.includes('Agent')) return '🤖';
    if (domainName.includes('Intent')) return '💬';
    if (domainName.includes('Adversarial')) return '🧱';
    return '📊';
  };

  const getCoverageColor = (percentage: number) => {
    if (percentage >= 80) return 'text-green-600';
    if (percentage >= 60) return 'text-yellow-600';
    if (percentage >= 40) return 'text-orange-600';
    return 'text-red-600';
  };

  // Filtering logic
  const matchesSearch = (item: any, searchTerm: string): boolean => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      item.name?.toLowerCase().includes(searchLower) ||
      item.description?.toLowerCase().includes(searchLower) ||
      item.purpose?.toLowerCase().includes(searchLower) ||
      item.layer?.toLowerCase().includes(searchLower)
    );
  };

  const matchesFilters = (technique: HierarchicalTechnique, category: HierarchicalCategory, domain: HierarchicalDomain, layer: HierarchicalLayer): boolean => {
    // Status filter
    if (filters.status !== 'all' && category.status !== filters.status) return false;

    // Layer filter
    if (filters.layer !== 'all') {
      const layerMatches = Object.keys(data?.layers || {}).some(layerName => {
        if (filters.layer === 'L1' && layerName.includes('L1')) return true;
        if (filters.layer === 'L2' && layerName.includes('L2')) return true;
        if (filters.layer === 'L3' && layerName.includes('L3')) return true;
        if (filters.layer === 'L4' && layerName.includes('L4')) return true;
        if (filters.layer === 'L5' && layerName.includes('L5')) return true;
        return false;
      });
      if (!layerMatches) return false;
    }

    // Coverage filter
    if (filters.coverage !== 'all') {
      const hasAnyMappings = technique.has_any_mappings;
      const maxCoverage = Math.max(...Object.values(technique.framework_mappings).map(m => m.coverage_percentage));
      
      if (filters.coverage === 'complete' && (!hasAnyMappings || maxCoverage < 80)) return false;
      if (filters.coverage === 'partial' && (!hasAnyMappings || maxCoverage < 40 || maxCoverage >= 80)) return false;
      if (filters.coverage === 'missing' && hasAnyMappings) return false;
    }

    // Framework filter
    if (filters.framework !== 'all') {
      const hasFrameworkMappings = Object.values(technique.framework_mappings).some(mapping => {
        if (filters.framework === 'ragas' && mapping.framework_type.toLowerCase() === 'ragas') return true;
        if (filters.framework === 'openai' && mapping.framework_type.toLowerCase().includes('openai')) return true;
        if (filters.framework === 'legacy' && mapping.framework_type.toLowerCase() === 'legacy') return true;
        return false;
      });
      if (!hasFrameworkMappings) return false;
    }

    return true;
  };

  const getFilteredData = (): HierarchicalMappingResponse | null => {
    if (!data) return null;

    const filteredLayers: { [layerName: string]: HierarchicalLayer } = {};

    Object.entries(data.layers).forEach(([layerName, layer]) => {
      const filteredDomains: HierarchicalDomain[] = [];

      layer.domains.forEach(domain => {
        const filteredCategories: HierarchicalCategory[] = [];

        domain.categories.forEach(category => {
          const filteredTechniques: HierarchicalTechnique[] = [];

          category.techniques.forEach(technique => {
            if (matchesSearch(technique, searchTerm) && matchesFilters(technique, category, domain, layer)) {
              filteredTechniques.push(technique);
            }
          });

          if (filteredTechniques.length > 0 || matchesSearch(category, searchTerm)) {
            filteredCategories.push({
              ...category,
              techniques: filteredTechniques,
              total_techniques: filteredTechniques.length
            });
          }
        });

        if (filteredCategories.length > 0 || matchesSearch(domain, searchTerm)) {
          const totalTechniques = filteredCategories.reduce((sum, cat) => sum + cat.total_techniques, 0);
          filteredDomains.push({
            ...domain,
            categories: filteredCategories,
            total_categories: filteredCategories.length,
            total_techniques: totalTechniques
          });
        }
      });

      if (filteredDomains.length > 0 || matchesSearch({ name: layerName }, searchTerm)) {
        const totalCategories = filteredDomains.reduce((sum, dom) => sum + dom.total_categories, 0);
        const totalTechniques = filteredDomains.reduce((sum, dom) => sum + dom.total_techniques, 0);
        
        filteredLayers[layerName] = {
          domains: filteredDomains,
          total_categories: totalCategories,
          total_techniques: totalTechniques
        };
      }
    });

    return {
      layers: filteredLayers,
      statistics: data.statistics
    };
  };

  const getStatusIcon = (isConfigured: boolean, coveragePercentage: number) => {
    if (isConfigured && coveragePercentage >= 80) return <CheckCircle className="h-4 w-4 text-green-500" />;
    if (isConfigured && coveragePercentage >= 40) return <AlertCircle className="h-4 w-4 text-yellow-500" />;
    if (isConfigured) return <AlertCircle className="h-4 w-4 text-orange-500" />;
    return <XCircle className="h-4 w-4 text-gray-400" />;
  };

  // Quick action handlers (now use the new modal functions)
  const handleAddMapping = async (technique: HierarchicalTechnique, frameworkId: number) => {
    await openCreateMappingModal(technique, frameworkId);
  };

  const handleEditMapping = async (technique: HierarchicalTechnique, frameworkId: number) => {
    await openEditMappingModal(technique, frameworkId);
  };

  const handleCopyMappings = (technique: HierarchicalTechnique, fromFrameworkId: number, toFrameworkId: number) => {
    // TODO: Implement bulk copy functionality
    addNotification('Copy mappings functionality coming soon!', 'info');
  };

  const handleBulkConfigure = (technique: HierarchicalTechnique) => {
    // TODO: Implement bulk configuration for all frameworks
    addNotification('Bulk configuration functionality coming soon!', 'info');
  };

  // Load framework metrics when opening modals
  const loadFrameworkMetrics = async (frameworkId: number) => {
    try {
      const response = await evaluationFrameworksApi.getFrameworkMetrics(frameworkId);
      console.log('Framework metrics response:', response);
      
      // Handle different response formats
      let metrics: any[] = [];
      if (Array.isArray(response)) {
        metrics = response;
      } else if (response && typeof response === 'object') {
        // Type assertion to access properties safely
        const responseObj = response as any;
        
        // Check for common response patterns
        if (Array.isArray(responseObj.metrics)) {
          metrics = responseObj.metrics;
        } else if (Array.isArray(responseObj.data)) {
          metrics = responseObj.data;
        } else if (Array.isArray(responseObj.results)) {
          metrics = responseObj.results;
        } else {
          console.warn('Unexpected response format:', response);
          metrics = [];
        }
      }
      
      console.log('Processed metrics:', metrics);
      setFrameworkMetrics(metrics);
    } catch (error) {
      console.error('Error loading framework metrics:', error);
      addNotification('Failed to load framework metrics', 'error');
      setFrameworkMetrics([]);
    }
  };

  // Handle saving a new or edited mapping
  const handleSaveMapping = async () => {
    if (!selectedTechnique || !selectedFramework) {
      addNotification('Missing technique or framework selection', 'error');
      return;
    }

    try {
      setLoading(true);

      // Create new mapping(s)
      if (selectedMetrics.length === 0 && !mappingFormData.metric_type) {
        addNotification('Please select at least one metric', 'error');
        setLoading(false);
        return;
      }

      // Create mappings for selected metrics
      const metricsToCreate = selectedMetrics.length > 0 
        ? selectedMetrics 
        : [parseInt(mappingFormData.metric_type)];

      for (const metricId of metricsToCreate) {
        const metric = frameworkMetrics.find(m => m.id === metricId);
        if (metric) {
          const createData: EvaluationMappingCreate = {
            technique_id: selectedTechnique.id,
            metric_type: metric.metric_type,
            is_required: mappingFormData.is_required,
            weight: mappingFormData.weight,
            priority: mappingFormData.priority,
            description: mappingFormData.description || '',
            framework_id: selectedFramework,
            framework_metric_id: metricId
          };
          await evaluationMappingsApi.createMapping(createData);
        }
      }
      addNotification(`Successfully created ${metricsToCreate.length} mapping(s)`, 'success');

      // Reset form and reload data
      setShowCreateMappingModal(false);
      setShowEditMappingModal(false);
      setSelectedTechnique(null);
      setSelectedFramework(null);
      setSelectedMetrics([]);
      setEditingMappingId(null);
      setMappingFormData({
        metric_type: '',
        is_required: true,
        weight: 1.0,
        priority: 1,
        description: ''
      });
      
      await loadData();
    } catch (error: any) {
      console.error('Error saving mapping:', error);
      addNotification(error.response?.data?.detail || 'Failed to save mapping', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Handle deleting a mapping
  const handleDeleteMappingById = async (mappingId: number) => {
    if (!window.confirm('Are you sure you want to delete this mapping?')) {
      return;
    }

    try {
      setLoading(true);
      await evaluationMappingsApi.deleteMapping(mappingId);
      addNotification('Mapping deleted successfully', 'success');
      await loadData();
    } catch (error: any) {
      console.error('Error deleting mapping:', error);
      addNotification(error.response?.data?.detail || 'Failed to delete mapping', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Open create modal
  const openCreateMappingModal = async (technique: HierarchicalTechnique, frameworkId: number) => {
    setSelectedTechnique(technique);
    setSelectedFramework(frameworkId);
    setEditingMappingId(null);
    setSelectedMetrics([]);
    setMappingFormData({
      metric_type: '',
      is_required: true,
      weight: 1.0,
      priority: 1,
      description: ''
    });
    await loadFrameworkMetrics(frameworkId);
    setShowCreateMappingModal(true);
  };

  // Open edit modal
  const openEditMappingModal = async (technique: HierarchicalTechnique, frameworkId: number) => {
    setSelectedTechnique(technique);
    setSelectedFramework(frameworkId);
    await loadFrameworkMetrics(frameworkId);
    setShowEditMappingModal(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-2 text-slate-400">Loading evaluation mappings...</p>
        </div>
      </div>
    );
  }

  if (!data || !coverageReport) {
    return (
      <div className="text-center py-8">
        <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <p className="text-slate-400">Failed to load evaluation mappings data</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header & Overview Dashboard */}
      <div className="bg-slate-800 rounded-lg shadow-sm border border-slate-700 p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-100">Evaluation Mappings Overview</h1>
            <p className="text-slate-400 mt-2">Complete hierarchical view of test techniques and their evaluation framework mappings</p>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={() => setViewMode(viewMode === 'overview' ? 'comparison' : 'overview')}
              className={`inline-flex items-center px-4 py-2 border text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                viewMode === 'overview'
                  ? 'border-purple-500 text-purple-200 bg-purple-900/30 focus:ring-purple-500'
                  : 'border-slate-600 text-slate-200 bg-slate-800 hover:bg-slate-700 focus:ring-purple-500'
              }`}
            >
              {viewMode === 'overview' ? <Eye className="h-4 w-4 mr-2" /> : <EyeOff className="h-4 w-4 mr-2" />}
              {viewMode === 'overview' ? 'Overview Mode' : 'Comparison Mode'}
            </button>
          </div>
        </div>

        {/* Statistics Dashboard */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-slate-800 rounded-lg p-4 border border-slate-700">
            <div className="flex items-center">
              <BarChart3 className="h-8 w-8 text-blue-400" />
              <div className="ml-4">
                <p className="text-sm font-medium text-slate-400">Total Techniques</p>
                <p className="text-2xl font-bold text-slate-100">{data.statistics.total_techniques}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-slate-800 rounded-lg p-4 border border-slate-700">
            <div className="flex items-center">
              <CheckCircle className="h-8 w-8 text-green-400" />
              <div className="ml-4">
                <p className="text-sm font-medium text-slate-400">Overall Coverage</p>
                <p className={`text-2xl font-bold text-slate-100 ${getCoverageColor(coverageReport.overall_coverage_percentage)}`}>
                  {coverageReport.overall_coverage_percentage.toFixed(1)}%
                </p>
                {/* Coverage Progress Bar */}
                <div className="w-full bg-slate-700 rounded-full h-2 mt-2">
                  <div 
                    className={`h-2 rounded-full transition-all duration-300 ${
                      coverageReport.overall_coverage_percentage >= 80 ? 'bg-green-500' :
                      coverageReport.overall_coverage_percentage >= 60 ? 'bg-yellow-500' :
                      coverageReport.overall_coverage_percentage >= 40 ? 'bg-orange-500' : 'bg-red-500'
                    }`}
                    style={{ width: `${Math.min(coverageReport.overall_coverage_percentage, 100)}%` }}
                  ></div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="bg-slate-800 rounded-lg p-4 border border-slate-700">
            <div className="flex items-center">
              <TrendingUp className="h-8 w-8 text-purple-400" />
              <div className="ml-4">
                <p className="text-sm font-medium text-slate-400">Total Mappings</p>
                <p className="text-2xl font-bold text-slate-100">{data.statistics.total_mappings}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-slate-800 rounded-lg p-4 border border-slate-700">
            <div className="flex items-center">
              <BarChart3 className="h-8 w-8 text-amber-400" />
              <div className="ml-4">
                <p className="text-sm font-medium text-slate-400">Layer Coverage</p>
                {(() => {
                  const layersWithTechniques = Object.values(coverageReport.layer_coverage || {}).filter(l => l.total_techniques > 0).length;
                  const totalLayers = 5; // We have L1-L5 defined
                  const layerCoveragePercent = (layersWithTechniques / totalLayers) * 100;
                  return (
                    <>
                      <p className={`text-2xl font-bold text-slate-100 ${getCoverageColor(layerCoveragePercent)}`}>
                        {layersWithTechniques}/{totalLayers}
                      </p>
                      <div className="flex items-center mt-1">
                        <span className="text-xs text-slate-400">
                          {layerCoveragePercent.toFixed(0)}% layers have techniques
                        </span>
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>

        {/* Coverage Summary */}
        <div className="bg-slate-800 rounded-lg p-6 mb-6 border border-slate-700">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-slate-100">Coverage Overview</h3>
            <div className="flex items-center space-x-4 text-sm">
              <div className="flex items-center">
                <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
                <span className="text-slate-400">Complete (≥80%)</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 bg-yellow-500 rounded-full mr-2"></div>
                <span className="text-slate-400">Partial (40-79%)</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 bg-red-500 rounded-full mr-2"></div>
                <span className="text-slate-400">Missing (&lt;40%)</span>
              </div>
            </div>
          </div>

          {/* Layer Coverage Breakdown */}
          <div className="mb-6">
            <h4 className="text-sm font-semibold text-slate-200 mb-3">Coverage by Layer</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
              {Object.entries(coverageReport.layer_coverage || {}).map(([layerKey, layer]) => {
                const coverage = layer.coverage_percentage || 0;
                return (
                  <div key={layerKey} className="bg-slate-800 rounded-lg p-3 border border-slate-700">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-medium text-slate-200">{layer.layer_name.split(':')[0]}</span>
                      <span className={`text-xs font-bold ${getCoverageColor(coverage)}`}>
                        {coverage.toFixed(0)}%
                      </span>
                    </div>
                    <div className="w-full bg-slate-700 rounded-full h-1.5 mb-2">
                      <div 
                        className={`h-1.5 rounded-full transition-all duration-300 ${
                          coverage >= 80 ? 'bg-green-500' :
                          coverage >= 60 ? 'bg-yellow-500' :
                          coverage >= 40 ? 'bg-orange-500' : 'bg-red-500'
                        }`}
                        style={{ width: `${Math.min(coverage, 100)}%` }}
                      ></div>
                    </div>
                    <div className="space-y-0.5 text-xs text-slate-400">
                      <div className="flex justify-between">
                        <span>Domains:</span>
                        <span className="font-medium">{layer.total_domains}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Categories:</span>
                        <span className="font-medium">{layer.total_categories}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Techniques:</span>
                        <span className="font-medium">{layer.total_techniques}</span>
                      </div>
                      <div className="flex justify-between pt-1 border-t border-slate-700">
                        <span className="text-green-400">✓ Mapped:</span>
                        <span className="font-medium text-green-400">{layer.techniques_with_mappings}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          
          {/* Framework Coverage Breakdown */}
          <div>
            <h4 className="text-sm font-semibold text-slate-200 mb-3">Coverage by Framework</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.entries(data.statistics.frameworks).map(([frameworkId, framework]) => {
              const frameworkCoverageData = coverageReport.framework_coverage[Number(frameworkId)];
              const frameworkCoverage = frameworkCoverageData?.coverage_percentage || 0;
              const techniquesMapped = frameworkCoverageData?.techniques_mapped || 0;
              const techniquesUnmapped = frameworkCoverageData?.techniques_unmapped || 0;
              
              return (
                <div key={frameworkId} className="bg-slate-800 rounded-lg p-4 border border-slate-700">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-slate-200">{framework.name}</span>
                    <span className={`text-sm font-bold ${getCoverageColor(frameworkCoverage)}`}>
                      {frameworkCoverage.toFixed(1)}%
                    </span>
                  </div>
                  <div className="w-full bg-slate-700 rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full transition-all duration-300 ${
                        frameworkCoverage >= 80 ? 'bg-green-500' :
                        frameworkCoverage >= 60 ? 'bg-yellow-500' :
                        frameworkCoverage >= 40 ? 'bg-orange-500' : 'bg-red-500'
                      }`}
                      style={{ width: `${Math.min(frameworkCoverage, 100)}%` }}
                    ></div>
                  </div>
                  <div className="flex justify-between text-xs text-slate-400 mt-1">
                    <span>{framework.total_mappings} mappings</span>
                    <span>{framework.available_metrics} metrics</span>
                  </div>
                  <div className="flex justify-between text-xs text-slate-400 mt-2 pt-2 border-t border-slate-700">
                    <span className="text-green-600">✓ {techniquesMapped} mapped</span>
                    <span className="text-red-600">✗ {techniquesUnmapped} unmapped</span>
                  </div>
                </div>
              );
            })}
            </div>
          </div>
        </div>

        {/* View Mode and Framework Toggle */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-4 space-y-4 lg:space-y-0">
          {/* View Mode Toggle */}
          <div className="flex items-center space-x-4">
            <span className="text-sm font-medium text-slate-200">View Mode:</span>
            <div className="flex bg-slate-700 rounded-lg p-1">
              <button
                onClick={() => setViewMode('overview')}
                className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                  viewMode === 'overview'
                    ? 'bg-slate-800 text-purple-300 shadow-sm'
                    : 'text-slate-400 hover:text-slate-100'
                }`}
              >
                Overview
              </button>
              <button
                onClick={() => setViewMode('comparison')}
                className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                  viewMode === 'comparison'
                    ? 'bg-slate-800 text-purple-300 shadow-sm'
                    : 'text-slate-400 hover:text-slate-100'
                }`}
              >
                Compare
              </button>
            </div>
          </div>

          {/* Framework Toggle Buttons */}
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium text-slate-200">
              {viewMode === 'comparison' ? 'Compare Frameworks:' : 'Show Frameworks:'}
            </span>
            <button
              onClick={() => setSelectedFrameworks([])}
              className={`inline-flex items-center px-3 py-1 border text-sm font-medium rounded-full ${
                selectedFrameworks.length === 0
                  ? 'border-purple-500 text-purple-200 bg-purple-900/30'
                  : 'border-slate-600 text-slate-200 bg-slate-800 hover:bg-slate-700'
              }`}
            >
              All
            </button>
            {Object.entries(data.statistics.frameworks).map(([frameworkId, framework]) => (
              <button
                key={frameworkId}
                onClick={() => {
                  if (viewMode === 'comparison') {
                    // In comparison mode, allow multiple frameworks
                    if (selectedFrameworks.includes(Number(frameworkId))) {
                      setSelectedFrameworks(selectedFrameworks.filter(id => id !== Number(frameworkId)));
                    } else {
                      setSelectedFrameworks([...selectedFrameworks, Number(frameworkId)]);
                    }
                  } else {
                    // In overview mode, toggle single framework
                    toggleFramework(Number(frameworkId));
                  }
                }}
                className={`inline-flex items-center px-3 py-1 border text-sm font-medium rounded-full ${
                  selectedFrameworks.includes(Number(frameworkId))
                    ? 'border-purple-500 text-purple-200 bg-purple-900/30'
                    : 'border-slate-600 text-slate-200 bg-slate-800 hover:bg-slate-700'
                }`}
              >
                <Settings className="h-3 w-3 mr-1" />
                {framework.name}
                <span className="ml-1 text-xs bg-slate-600 text-slate-300 px-1 rounded">
                  {framework.total_mappings}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Search and Filters */}
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Search techniques, categories, domains..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-slate-600 rounded-md bg-slate-800 text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`inline-flex items-center px-4 py-2 border text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 ${
              showFilters
                ? 'border-purple-500 text-purple-200 bg-purple-900/30 focus:ring-purple-500'
                : 'border-slate-600 text-slate-200 bg-slate-800 hover:bg-slate-700 focus:ring-purple-500'
            }`}
          >
            <Filter className="h-4 w-4 mr-2" />
            Filters
          </button>
        </div>

        {/* Advanced Filters */}
        {showFilters && (
          <div className="bg-slate-800 rounded-lg p-4 space-y-4 border border-slate-700">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Status Filter */}
              <div>
                <label className="block text-sm font-medium text-slate-200 mb-1">Status</label>
                <select
                  value={filters.status}
                  onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-600 rounded-md bg-slate-800 text-slate-100 focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
                >
                  <option value="all">All Statuses</option>
                  <option value="approved">Approved</option>
                  <option value="pending">Pending</option>
                  <option value="declined">Declined</option>
                </select>
              </div>

              {/* Coverage Filter */}
              <div>
                <label className="block text-sm font-medium text-slate-200 mb-1">Coverage</label>
                <select
                  value={filters.coverage}
                  onChange={(e) => setFilters({ ...filters, coverage: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-600 rounded-md bg-slate-800 text-slate-100 focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
                >
                  <option value="all">All Coverage</option>
                  <option value="complete">Complete (≥80%)</option>
                  <option value="partial">Partial (40-79%)</option>
                  <option value="missing">Missing (&lt;40%)</option>
                </select>
              </div>

              {/* Layer Filter */}
              <div>
                <label className="block text-sm font-medium text-slate-200 mb-1">Layer</label>
                <select
                  value={filters.layer}
                  onChange={(e) => setFilters({ ...filters, layer: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-600 rounded-md bg-slate-800 text-slate-100 focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
                >
                  <option value="all">All Layers</option>
                  <option value="L1">L1: Knowledge Layer</option>
                  <option value="L2">L2: Retrieval Layer</option>
                  <option value="L3">L3: Reasoning Layer</option>
                  <option value="L4">L4: Interaction Layer</option>
                  <option value="L5">L5: Robustness Layer</option>
                </select>
              </div>

              {/* Framework Filter */}
              <div>
                <label className="block text-sm font-medium text-slate-200 mb-1">Framework</label>
                <select
                  value={filters.framework}
                  onChange={(e) => setFilters({ ...filters, framework: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-600 rounded-md bg-slate-800 text-slate-100 focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
                >
                  <option value="all">All Frameworks</option>
                  <option value="ragas">RAGAS</option>
                  <option value="openai">OpenAI Judge</option>
                  <option value="legacy">Legacy</option>
                </select>
              </div>
            </div>

            {/* Clear Filters */}
            <div className="flex justify-end">
              <button
                onClick={() => setFilters({ status: 'all', coverage: 'all', layer: 'all', framework: 'all' })}
                className="text-sm text-slate-400 hover:text-slate-200 underline"
              >
                Clear all filters
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Hierarchical Tree */}
      <div className="bg-slate-800 rounded-lg shadow-sm border border-slate-700">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-slate-100">Test Hierarchy & Mappings</h2>
            {searchTerm || Object.values(filters).some(f => f !== 'all') && (
              <span className="text-sm text-slate-400">
                Showing filtered results
              </span>
            )}
          </div>
          
          {viewMode === 'comparison' && selectedFrameworks.length > 1 ? (
            /* Comparison Mode */
            <div className="space-y-6">
              {Object.entries(getFilteredData()?.layers || {}).map(([layerName, layer]) => (
                <div key={layerName} className="border border-slate-700 rounded-lg">
                  {/* Layer Header for Comparison */}
                  <div className="bg-slate-700/50 px-4 py-3 border-b border-slate-700">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <span className="text-lg mr-2">{getLayerIcon(layerName)}</span>
                        <h3 className="text-lg font-semibold text-slate-100">{layerName}</h3>
                      </div>
                      <div className="flex items-center space-x-4 text-sm text-slate-400">
                        <span>{layer.total_categories} categories</span>
                        <span>{layer.total_techniques} techniques</span>
                      </div>
                    </div>
                  </div>

                  {/* Framework Comparison Headers */}
                  <div className="bg-slate-800 border-b border-slate-700">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 p-4">
                      <div className="font-medium text-slate-200">Technique</div>
                      {selectedFrameworks.map(frameworkId => (
                        <div key={frameworkId} className="text-center">
                          <span className="text-sm font-medium text-slate-200">
                            {data?.statistics.frameworks[frameworkId]?.name}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Techniques Comparison */}
                  <div className="divide-y divide-slate-700">
                    {layer.domains.map(domain => 
                      domain.categories.map(category => 
                        category.techniques.map(technique => (
                          <div key={technique.id} className="p-4 hover:bg-slate-700/30">
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 items-center">
                              {/* Technique Info */}
                              <div>
                                <div className="flex items-center space-x-2">
                                  <span className="text-sm font-medium text-slate-100">
                                    {technique.name}
                                  </span>
                                </div>
                                <div className="text-xs text-slate-400 mt-1">
                                  {category.name} • {domain.name}
                                </div>
                              </div>

                              {/* Framework Comparisons */}
                              {selectedFrameworks.map(frameworkId => {
                                const mapping = technique.framework_mappings[frameworkId];
                                return (
                                  <div key={frameworkId} className="text-center">
                                    {mapping ? (
                                      <div className="space-y-2">
                                        <div className="flex items-center justify-center space-x-1">
                                          {getStatusIcon(mapping.is_configured, mapping.coverage_percentage)}
                                          <span className={`text-sm font-medium ${getCoverageColor(mapping.coverage_percentage)}`}>
                                            {mapping.coverage_percentage.toFixed(0)}%
                                          </span>
                                        </div>
                                        <div className="flex flex-wrap justify-center gap-1">
                                          {mapping.metrics.slice(0, 3).map(metric => (
                                            <span key={metric.id} className="inline-flex items-center px-1.5 py-0.5 text-xs font-medium rounded bg-blue-900/50 text-blue-200">
                                              {metric.metric_type}
                                            </span>
                                          ))}
                                          {mapping.metrics.length > 3 && (
                                            <span className="inline-flex items-center px-1.5 py-0.5 text-xs font-medium rounded bg-slate-600 text-slate-300">
                                              +{mapping.metrics.length - 3}
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    ) : (
                                      <div className="text-center">
                                        <XCircle className="h-4 w-4 text-slate-400 mx-auto mb-1" />
                                        <span className="text-xs text-slate-400">No mappings</span>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        ))
                      )
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            /* Overview Mode */
            Object.entries(getFilteredData()?.layers || {}).map(([layerName, layer]) => (
            <div key={layerName} className="mb-6">
              {/* Layer Header */}
              <div 
                className="flex items-center cursor-pointer hover:bg-slate-700/30 p-3 rounded-lg"
                onClick={() => toggleLayer(layerName)}
              >
                {expandedLayers.has(layerName) ? (
                  <ChevronDown className="h-5 w-5 text-slate-400 mr-2" />
                ) : (
                  <ChevronRight className="h-5 w-5 text-slate-400 mr-2" />
                )}
                <span className="text-2xl mr-3">{getLayerIcon(layerName)}</span>
                <div>
                  <h3 className="text-lg font-semibold text-slate-100">{layerName}</h3>
                  <p className="text-sm text-slate-400">
                    {layer.total_categories} categories • {layer.total_techniques} techniques
                  </p>
                </div>
              </div>

              {/* Domains */}
              {expandedLayers.has(layerName) && (
                <div className="ml-8 space-y-4">
                  {layer.domains.map((domain) => (
                    <div key={domain.id} className="border-l-2 border-slate-700 pl-4">
                      {/* Domain Header */}
                      <div 
                        className="flex items-center cursor-pointer hover:bg-slate-700/30 p-2 rounded-lg"
                        onClick={() => toggleDomain(domain.id)}
                      >
                        {expandedDomains.has(domain.id) ? (
                          <ChevronDown className="h-4 w-4 text-slate-400 mr-2" />
                        ) : (
                          <ChevronRight className="h-4 w-4 text-slate-400 mr-2" />
                        )}
                        <span className="text-xl mr-2">{getDomainIcon(domain.name)}</span>
                        <div>
                          <h4 className="font-medium text-slate-100">{domain.name}</h4>
                          <p className="text-sm text-slate-400">
                            {domain.total_categories} categories • {domain.total_techniques} techniques
                          </p>
                        </div>
                      </div>

                      {/* Categories */}
                      {expandedDomains.has(domain.id) && (
                        <div className="ml-6 space-y-3">
                          {domain.categories.map((category) => (
                            <div key={category.id} className="border-l-2 border-slate-600 pl-4">
                              {/* Category Header */}
                              <div 
                                className="flex items-center cursor-pointer hover:bg-slate-700/30 p-2 rounded-lg"
                                onClick={() => toggleCategory(category.id)}
                              >
                                {expandedCategories.has(category.id) ? (
                                  <ChevronDown className="h-4 w-4 text-slate-400 mr-2" />
                                ) : (
                                  <ChevronRight className="h-4 w-4 text-slate-400 mr-2" />
                                )}
                                <div className="flex-1">
                                  <h5 className="font-medium text-slate-100">{category.name}</h5>
                                  <p className="text-sm text-slate-400">
                                    {category.total_techniques} techniques
                                    {category.status && (
                                      <span className={`ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                        category.status === 'approved' ? 'bg-green-900/50 text-green-200' :
                                        category.status === 'pending' ? 'bg-amber-900/50 text-amber-200' :
                                        'bg-red-900/50 text-red-200'
                                      }`}>
                                        {category.status}
                                      </span>
                                    )}
                                  </p>
                                </div>
                              </div>

                              {/* Techniques */}
                              {expandedCategories.has(category.id) && (
                                <div className="ml-6 space-y-2">
                                  {category.techniques.map((technique) => (
                                    <div key={technique.id} className="border border-slate-700 rounded-lg p-4">
                                      {/* Technique Header */}
                                      <div 
                                        className="flex items-center justify-between cursor-pointer"
                                        onClick={() => toggleTechnique(technique.id)}
                                      >
                                        <div className="flex items-center">
                                          {expandedTechniques.has(technique.id) ? (
                                            <ChevronDown className="h-4 w-4 text-slate-400 mr-2" />
                                          ) : (
                                            <ChevronRight className="h-4 w-4 text-slate-400 mr-2" />
                                          )}
                                          <h6 className="font-medium text-slate-100">{technique.name}</h6>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                          <span className="text-sm text-slate-400">
                                            {technique.total_mappings} mappings
                                          </span>
                                          <div className="flex items-center space-x-1">
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleBulkConfigure(technique);
                                              }}
                                              className="text-purple-300 hover:text-purple-200 p-1 rounded hover:bg-purple-900/30"
                                              title="Bulk configure mappings"
                                            >
                                              <Settings className="h-4 w-4" />
                                            </button>
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                // Add mapping to first available framework
                                                const firstFramework = selectedFrameworks[0];
                                                if (firstFramework) {
                                                  handleAddMapping(technique, firstFramework);
                                                }
                                              }}
                                              className="text-green-400 hover:text-green-300 p-1 rounded hover:bg-green-900/30"
                                              title="Add mapping"
                                            >
                                              <Plus className="h-4 w-4" />
                                            </button>
                                          </div>
                                        </div>
                                      </div>

                                      {/* Framework Mappings */}
                                      {expandedTechniques.has(technique.id) && (
                                        <div className="mt-4 space-y-3">
                                          {Object.entries(technique.framework_mappings).map(([frameworkId, mapping]) => {
                                            if (!selectedFrameworks.includes(Number(frameworkId))) return null;
                                            
                                            return (
                                              <div key={frameworkId} className="bg-slate-700/50 rounded-lg p-3 border border-slate-600">
                                                <div className="flex items-center justify-between mb-2">
                                                  <div className="flex items-center space-x-2">
                                                    {getStatusIcon(mapping.is_configured, mapping.coverage_percentage)}
                                                    <span className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full ${getFrameworkColor(mapping.framework_type)}`}>
                                                      {mapping.framework_name}
                                                    </span>
                                                    <span className="text-sm text-slate-400">
                                                      {mapping.mapped_metrics}/{mapping.available_metrics} metrics
                                                    </span>
                                                  </div>
                                                  <span className={`text-sm font-medium ${getCoverageColor(mapping.coverage_percentage)}`}>
                                                    {mapping.coverage_percentage.toFixed(0)}%
                                                  </span>
                                                </div>
                                                
                                                {/* Metrics */}
                                                {mapping.metrics.length > 0 && (
                                                  <div className="flex flex-wrap gap-2">
                                                    {mapping.metrics.map((metric) => (
                                                      <span 
                                                        key={metric.id}
                                                        className="inline-flex items-center px-2 py-1 text-xs font-medium rounded bg-slate-800 border border-slate-600 text-slate-200"
                                                      >
                                                        {metric.metric_type}
                                                        {metric.is_required && (
                                                          <span className="ml-1 text-green-400">*</span>
                                                        )}
                                                      </span>
                                                    ))}
                                                  </div>
                                                )}
                                                
                                                {mapping.metrics.length === 0 && (
                                                  <p className="text-sm text-slate-400 italic">No metrics configured</p>
                                                )}

                                                {/* Quick Actions */}
                                                <div className="flex items-center space-x-2 mt-3 pt-2 border-t border-slate-600">
                                                  <button
                                                    onClick={() => handleAddMapping(technique, Number(frameworkId))}
                                                    className="inline-flex items-center px-2 py-1 text-xs font-medium text-green-200 bg-green-900/50 hover:bg-green-800/50 rounded"
                                                  >
                                                    <Plus className="h-3 w-3 mr-1" />
                                                    Add Metric
                                                  </button>
                                                  
                                                  {mapping.metrics.length > 0 && (
                                                    <button
                                                      onClick={() => handleEditMapping(technique, Number(frameworkId))}
                                                      className="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-200 bg-blue-900/50 hover:bg-blue-800/50 rounded"
                                                    >
                                                      <Settings className="h-3 w-3 mr-1" />
                                                      Edit
                                                    </button>
                                                  )}

                                                  {/* Copy to other frameworks */}
                                                  {mapping.metrics.length > 0 && (
                                                    <div className="flex items-center space-x-1">
                                                      {selectedFrameworks.filter(fid => Number(fid) !== Number(frameworkId)).map(otherFrameworkId => (
                                                        <button
                                                          key={otherFrameworkId}
                                                          onClick={() => handleCopyMappings(technique, Number(frameworkId), otherFrameworkId)}
                                                          className="inline-flex items-center px-2 py-1 text-xs font-medium text-purple-200 bg-purple-900/50 hover:bg-purple-800/50 rounded"
                                                          title={`Copy to ${data?.statistics.frameworks[otherFrameworkId]?.name || 'Framework'}`}
                                                        >
                                                          Copy to {data?.statistics.frameworks[otherFrameworkId]?.name?.split(' ')[0] || 'Other'}
                                                        </button>
                                                      ))}
                                                    </div>
                                                  )}
                                                </div>
                                              </div>
                                            );
                                          })}
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))
          )}
        </div>
      </div>

      {/* Create Mapping Modal */}
      {showCreateMappingModal && selectedTechnique && selectedFramework && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-6 border w-full max-w-2xl shadow-lg rounded-md bg-slate-800 border border-slate-700 my-8">
            <div className="space-y-4">
              {/* Header */}
              <div className="flex items-center justify-between pb-4 border-b border-slate-700">
                <div>
                  <h3 className="text-xl font-semibold text-slate-100">
                    Add Metrics for {selectedTechnique.name}
                  </h3>
                  <p className="text-sm text-slate-400 mt-1">
                    Framework: <span className="font-medium">{data?.statistics.frameworks[selectedFramework]?.name}</span>
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowCreateMappingModal(false);
                    setSelectedTechnique(null);
                    setSelectedFramework(null);
                  }}
                  className="text-slate-400 hover:text-slate-200"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Metrics Selection */}
              <div>
                <label className="block text-sm font-medium text-slate-200 mb-2">
                  Select Metrics to Add *
                </label>
                <div className="border border-slate-600 rounded-md p-4 max-h-64 overflow-y-auto">
                  {frameworkMetrics.length === 0 ? (
                    <p className="text-sm text-slate-400 text-center py-4">No metrics available for this framework</p>
                  ) : (
                    <div className="space-y-2">
                      {frameworkMetrics.map((metric) => (
                        <label key={metric.id} className="flex items-center p-2 hover:bg-slate-700/50 rounded cursor-pointer">
                          <input
                            type="checkbox"
                            checked={selectedMetrics.includes(metric.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedMetrics([...selectedMetrics, metric.id]);
                              } else {
                                setSelectedMetrics(selectedMetrics.filter(id => id !== metric.id));
                              }
                            }}
                            className="h-4 w-4 text-indigo-600 focus:ring-purple-500 border-slate-600 rounded"
                          />
                          <span className="ml-3 text-sm font-medium text-slate-100">{metric.metric_type}</span>
                          {metric.description && (
                            <span className="ml-2 text-xs text-slate-400">- {metric.description}</span>
                          )}
                        </label>
                      ))}
                    </div>
                  )}
                </div>
                <p className="mt-2 text-sm text-slate-400">
                  Selected: {selectedMetrics.length} metric(s)
                </p>
              </div>

              {/* Configuration Options */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-200 mb-1">
                    Is Required
                  </label>
                  <select
                    value={mappingFormData.is_required ? 'true' : 'false'}
                    onChange={(e) => setMappingFormData({ ...mappingFormData, is_required: e.target.value === 'true' })}
                    className="w-full px-3 py-2 border border-slate-600 rounded-md bg-slate-800 text-slate-100 focus:outline-none focus:ring-1 focus:ring-purple-500"
                  >
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-200 mb-1">
                    Weight
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    max="10"
                    value={mappingFormData.weight}
                    onChange={(e) => setMappingFormData({ ...mappingFormData, weight: parseFloat(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-slate-600 rounded-md bg-slate-800 text-slate-100 focus:outline-none focus:ring-1 focus:ring-purple-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-200 mb-1">
                    Priority
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="10"
                    value={mappingFormData.priority}
                    onChange={(e) => setMappingFormData({ ...mappingFormData, priority: parseInt(e.target.value) || 1 })}
                    className="w-full px-3 py-2 border border-slate-600 rounded-md bg-slate-800 text-slate-100 focus:outline-none focus:ring-1 focus:ring-purple-500"
                  />
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-slate-200 mb-1">
                  Description (Optional)
                </label>
                <textarea
                  rows={3}
                  value={mappingFormData.description}
                  onChange={(e) => setMappingFormData({ ...mappingFormData, description: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-600 rounded-md bg-slate-800 text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-purple-500"
                  placeholder="Add any additional notes about these mappings..."
                />
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end space-x-3 pt-4 border-t border-slate-700">
                <button
                  onClick={() => {
                    setShowCreateMappingModal(false);
                    setSelectedTechnique(null);
                    setSelectedFramework(null);
                  }}
                  className="px-4 py-2 border border-slate-600 rounded-md text-slate-200 hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveMapping}
                  disabled={selectedMetrics.length === 0 || loading}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Creating...' : `Create ${selectedMetrics.length} Mapping(s)`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Mapping Modal */}
      {showEditMappingModal && selectedTechnique && selectedFramework && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-6 border w-full max-w-3xl shadow-lg rounded-md bg-slate-800 border border-slate-700 my-8">
            <div className="space-y-4">
              {/* Header */}
              <div className="flex items-center justify-between pb-4 border-b border-slate-700">
                <div>
                  <h3 className="text-xl font-semibold text-slate-100">
                    Manage Mappings: {selectedTechnique.name}
                  </h3>
                  <p className="text-sm text-slate-400 mt-1">
                    Framework: <span className="font-medium">{data?.statistics.frameworks[selectedFramework]?.name}</span>
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowEditMappingModal(false);
                    setSelectedTechnique(null);
                    setSelectedFramework(null);
                  }}
                  className="text-slate-400 hover:text-slate-200"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Current Mappings */}
              <div>
                <h4 className="text-sm font-medium text-slate-200 mb-3">Current Metric Mappings</h4>
                {(() => {
                  const mapping = selectedTechnique.framework_mappings[selectedFramework];
                  if (!mapping || mapping.metrics.length === 0) {
                    return (
                      <div className="text-center py-8 bg-slate-700/50 rounded-lg border-2 border-dashed border-slate-600">
                        <XCircle className="h-12 w-12 text-slate-400 mx-auto mb-2" />
                        <p className="text-sm text-slate-400">No metrics mapped yet</p>
                        <p className="text-xs text-slate-400 mt-1">Use the "Add Metric" button to create mappings</p>
                      </div>
                    );
                  }

                  return (
                    <div className="space-y-2 max-h-96 overflow-y-auto">
                      {mapping.metrics.map((metric) => (
                        <div key={metric.id} className="flex items-center justify-between p-4 bg-slate-700/50 border border-slate-600 rounded-lg hover:bg-slate-700/70 transition-colors">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2">
                              <span className="font-medium text-slate-100">{metric.metric_type}</span>
                              {metric.is_required && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-900/50 text-green-200">
                                  Required
                                </span>
                              )}
                            </div>
                            <div className="mt-1 flex items-center space-x-4 text-sm text-slate-400">
                              <span>Weight: {metric.weight}</span>
                              <span>Priority: {metric.priority}</span>
                            </div>
                            {metric.description && (
                              <p className="mt-2 text-sm text-slate-400">{metric.description}</p>
                            )}
                          </div>
                          <button
                            onClick={() => handleDeleteMappingById(metric.id)}
                            className="ml-4 p-2 text-red-400 hover:bg-red-900/30 rounded transition-colors"
                            title="Delete mapping"
                          >
                            <Trash2 className="h-5 w-5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>

              {/* Add More Metrics Section */}
              <div className="pt-4 border-t border-slate-700">
                <button
                  onClick={() => {
                    setShowEditMappingModal(false);
                    openCreateMappingModal(selectedTechnique, selectedFramework);
                  }}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add More Metrics
                </button>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end pt-4 border-t border-slate-700">
                <button
                  onClick={() => {
                    setShowEditMappingModal(false);
                    setSelectedTechnique(null);
                    setSelectedFramework(null);
                  }}
                  className="px-4 py-2 border border-slate-600 rounded-md text-slate-200 hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EvaluationMappingsOverview;
